import React from 'react';
import { PlayCircle, Image, File, AlertCircle } from 'lucide-react';
import VideoScrubPreview from './VideoScrubPreview';

interface Asset {
  id: string;
  name?: string;
  title?: string;
  media_type?: string;
  asset_type?: string;
  thumbnail_url?: string;
  thumbnailUrl?: string;
  sas_url?: string;
  url?: string;
  contentUrl?: string;
  tags?: string[];
}

interface AssetCardProps {
  asset: Asset;
  onQC?: (assetId: string) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, onQC }) => {
  const [imageError, setImageError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const displayName = asset.title || asset.name || 'Untitled Asset';
  const mediaType = (asset.media_type || asset.asset_type || '').toLowerCase();

  const getIcon = () => {
    switch (mediaType) {
      case 'video': return <PlayCircle size={48} className="asset-icon" />;
      case 'image': return <Image size={48} className="asset-icon" />;
      default: return <File size={48} className="asset-icon" />;
    }
  };

  const getThumbnailSource = (): string | null => {
    // Priority: explicit thumbnail > SAS URL (for images) > raw URL
    // Treat empty strings as null
    if (asset.thumbnail_url?.trim()) return asset.thumbnail_url;
    if (asset.thumbnailUrl?.trim()) return asset.thumbnailUrl;

    // For images, the sas_url IS the viewable content
    if (mediaType === 'image') {
      return asset.sas_url || asset.url || asset.contentUrl || null;
    }

    // For videos, sas_url is a video file — not usable as <img> src
    return null;
  };

  const thumbSrc = getThumbnailSource();
  // For video/3D assets, use sas_url for hover-to-scrub
  const videoScrubUrl = (mediaType === 'video' || mediaType === 'model_3d' || mediaType === '3d') ? (asset.sas_url?.trim() || null) : null;

  return (
    <div className="asset-card" onClick={() => onQC?.(asset.id)}>
      <div className="thumbnail">
        {videoScrubUrl ? (
          <VideoScrubPreview
            thumbnailUrl={thumbSrc || undefined}
            videoUrl={videoScrubUrl}
            alt={displayName}
          />
        ) : thumbSrc && !imageError ? (
          <>
            {isLoading && (
              <div className="thumbnail-loading">
                {getIcon()}
              </div>
            )}
            <img
              src={thumbSrc}
              alt={displayName}
              onLoad={() => setIsLoading(false)}
              onError={() => { setImageError(true); setIsLoading(false); }}
              style={{ display: isLoading ? 'none' : 'block' }}
            />
          </>
        ) : imageError ? (
          <div className="thumbnail-error">
            <AlertCircle size={24} />
            <span>Failed to load</span>
          </div>
        ) : (
          getIcon()
        )}
        {mediaType === 'video' && !videoScrubUrl && !imageError && (
          <div className="video-badge">
            <PlayCircle size={16} />
          </div>
        )}
      </div>
      <div className="details">
        <h4 title={displayName}>{displayName}</h4>
        {mediaType && <span className="media-type-badge">{mediaType}</span>}
        <div className="tags">
          {asset.tags?.slice(0, 3).map(tag => (
            <span key={tag} className="tag">{tag}</span>
          ))}
          {asset.tags && asset.tags.length > 3 && (
            <span className="tag tag-more">+{asset.tags.length - 3}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetCard;
